import { FC, useState } from 'react';

import { ChevronDown, ChevronUp } from 'lucide-react';

import { formatOrderAmount } from './orderRevisionUtils';
import { ScalePreview } from '../../../components/shared/ScalePreview.tsx';
import { Image } from '../../../components/ui/Image';
import { OrdersService } from '../../../services/OrdersService';
import { OfferProperty, OrderItem } from '../../../types';

const COLLAPSE_THRESHOLD = 3;

interface Props {
    items?: OrderItem[];
    label: string;
    offerProperties: OfferProperty[];
}

export const OrderItemsList: FC<Props> = ({
    items,
    label,
    offerProperties,
}) => {
    const [expanded, setExpanded] = useState(false);
    const shouldCollapse = (items?.length ?? 0) > COLLAPSE_THRESHOLD;
    const visibleItems =
        shouldCollapse && !expanded
            ? items?.slice(0, COLLAPSE_THRESHOLD)
            : items;

    return (
        <div className="mt-3 space-y-2">
            <div className="mb-2 text-xs font-medium text-muted-foreground">
                {label}
            </div>
            <div className="space-y-1.5">
                {visibleItems?.map((item, index) => {
                    const offerPropertyEntries =
                        OrdersService.getOfferPropertyEntries(
                            item.offer?.property_set,
                            offerProperties,
                        );
                    const offerPropertiesLabel = offerPropertyEntries
                        .map((entry) => `${entry.key}: ${entry.value}`)
                        .join(', ');

                    return (
                        <div
                            key={index}
                            className="flex items-center gap-3 rounded-md bg-muted/30 p-2"
                        >
                            <ScalePreview triggerClassName="relative h-8 w-6 flex-shrink-0 overflow-hidden rounded bg-muted">
                                <Image
                                    src={
                                        item.product?.image ||
                                        '/placeholder.svg'
                                    }
                                    alt={
                                        item.product?.image_alt ||
                                        item.product?.name
                                    }
                                    className="object-cover"
                                />
                            </ScalePreview>
                            <div className="min-w-0 flex-1">
                                <p className="line-clamp-1 text-sm font-medium">
                                    {item.product?.name}
                                </p>
                                <p className="line-clamp-1 text-xs text-muted-foreground">
                                    Кількість: {item.amount}
                                    {offerPropertiesLabel &&
                                        `, ${offerPropertiesLabel}`}
                                </p>
                            </div>
                            <div className="text-right text-sm font-semibold">
                                {formatOrderAmount(item.price_per_item)}
                            </div>
                        </div>
                    );
                })}
            </div>
            {shouldCollapse && (
                <button
                    type="button"
                    onClick={() => setExpanded(!expanded)}
                    className="flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                >
                    {expanded ? 'Згорнути' : `Показати всі (${items?.length})`}
                    {expanded ? (
                        <ChevronUp className="h-3 w-3" />
                    ) : (
                        <ChevronDown className="h-3 w-3" />
                    )}
                </button>
            )}
        </div>
    );
};
